import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { PublicLayout } from "@/components/PublicLayout";
import { Spinner } from "@/components/kit";
import { startGoogleLogin } from "@/hooks/useGoogleAuth";
import { getClaim } from "@/lib/claim";
import { ResendReceipt } from "@/components/ResendReceipt";
import { CheckCircle2 } from "lucide-react";

export default function Login({ signup = false }) {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(signup || params.get("mode") === "register" ? "register" : "login");
  usePageMeta(signup
    ? { title: "Sign up for Mindful Me — Create your account", description: "Create your Mindful Me account to track your mood, journal privately, learn coping skills and invite trusted supporters.", path: "/signup" }
    : { title: "Log in to Mindful Me", description: "Log in to Mindful Me, your calm companion for mood check-ins, private journaling, coping skills and support.", path: "/login" });
  const [claim, setClaim] = useState(undefined);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const go = useCallback((u) => nav(u?.has_paid ? "/" : "/paywall", { replace: true }), [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mode === "login" ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      toast.success(mode === "login" ? "Welcome back 💜" : "Account created 🎉");
      go(u);
    } catch (err) {
      toast.error(errMsg(err, "Authentication failed"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    getClaim().then((c) => {
      setClaim(c);
      if (c?.email) setForm((f) => (f.email ? f : { ...f, email: c.email }));
    });
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <PublicLayout>
      <section className="max-w-md mx-auto px-5 pt-14 mm-fade-up">
        <div className="mm-card p-8">
          <div className="text-4xl">🧠</div>
          {mode === "register" && claim === undefined ? <Spinner /> : mode === "register" && !claim ? (
            <div data-testid="register-pay-first">
              <h1 className="text-3xl font-bold mt-3">Pay first, then sign up</h1>
              <p className="text-mm-muted text-sm mt-2">Mindful Me is a one-time $19.99 lifetime unlock. Complete your payment and you'll create your account right after.</p>
              <Link to="/checkout" className="mm-btn w-full mt-6" data-testid="register-go-checkout">Continue to payment</Link>
              <button type="button" className="text-mm-primary font-semibold text-sm mt-6" data-testid="login-toggle-mode" onClick={() => setMode("login")}>I already have an account</button>
            </div>
          ) : (<>
          {mode === "register" && (
            <div className="mm-soft px-4 py-3 mt-4 text-sm flex items-center gap-2" data-testid="register-payment-confirmed"><CheckCircle2 size={16} className="text-mm-success" />Payment confirmed — your new account will be unlocked.</div>
          )}
          <h1 className="text-3xl font-bold mt-3">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="text-mm-muted text-sm mt-1">{mode === "login" ? "Log in to continue your journey." : "Start understanding your patterns today."}</p>
          <form onSubmit={submit} className="space-y-3 mt-6">
            {mode === "register" && <input className="mm-input" placeholder="Your name" required value={form.name} onChange={set("name")} data-testid="register-name-input" />}
            <input className="mm-input" type="email" placeholder="Email" required value={form.email} onChange={set("email")} data-testid="login-email-input" />
            <input className="mm-input" type="password" placeholder="Password" required value={form.password} onChange={set("password")} data-testid="login-password-input" />
            {mode === "register" && <p className="text-xs text-mm-muted">8+ characters with an uppercase letter, a lowercase letter and a number.</p>}
            <button type="submit" className="mm-btn w-full" disabled={busy} data-testid="login-submit-button">
              {busy && <Loader2 size={16} className="animate-spin" />}{mode === "login" ? "Log in" : "Create account"}
            </button>
          </form>
          <div className="flex items-center gap-3 my-5 text-xs text-mm-muted"><div className="h-px flex-1 bg-mm-line" />or<div className="h-px flex-1 bg-mm-line" /></div>
          <button onClick={startGoogleLogin} className="mm-btn-ghost w-full" data-testid="login-google-button">Continue with Google</button>
          <div className="flex justify-between text-sm mt-6">
            <button type="button" className="text-mm-primary font-semibold" data-testid="login-toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}>
              {mode === "login" ? "Create an account" : "I already have an account"}
            </button>
            {mode === "login" && <Link to="/forgot-password" className="text-mm-muted" data-testid="login-forgot-password-link">Forgot password?</Link>}
          </div>
          {mode === "login" && <div className="mt-4 text-center"><ResendReceipt /></div>}
          </>)}
        </div>
      </section>
    </PublicLayout>
  );
}
